import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, History, Home, Radar, Fingerprint, Crown, Repeat, Inbox, MessageCircle, Settings } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Scanner } from "@/components/Scanner";
import { ScanResult } from "@/components/ScanResult";
import { ThreatMap } from "@/components/ThreatMap";
import { Footprint } from "@/components/Footprint";
import { Leads } from "@/components/Leads";
import { WhatsAppSim } from "@/components/WhatsAppSim";
import { scanPdfBase64 } from "@/lib/pdf";

export default function Dashboard() {
  const { user, logout, updateUser, refreshUser } = useAuth();
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("scanner");

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/scan/history");
      setHistory(data);
    } catch (e) {
      console.error("No se pudo cargar el historial de escaneos:", e);
    }
  };

  useEffect(() => {
    loadHistory();
    refreshUser?.();
  }, []);

  const handleResult = async (data) => {
    setResult(data);
    loadHistory();
    refreshUser?.();
    if (data.priority && !data.report_emailed) {
      try {
        const pdf = await scanPdfBase64(data);
        await api.post("/scan/email-report", { scan_id: data.id, pdf_base64: pdf });
        toast.success("Informe PDF enviado por email 📩");
      } catch (e) {
        console.error("No se pudo enviar el informe por email:", e);
        toast.error("No se pudo enviar el informe por email");
      }
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo abrir el portal de cliente");
    }
  };

  const gradeColor = (g) =>
    ({ A: "#22e07a", B: "#7ee787", C: "#ffd166", D: "#ff9f43", F: "#ff2d6f" }[g] || "#00f0ff");

  return (
    <div className="relative z-10 min-h-screen">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#05070f]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-2.5" data-testid="dash-logo">
            <img src="/cyvr-logo.png" alt="CYVR" className="h-8 w-8 object-contain drop-shadow-[0_0_10px_rgba(0,240,255,0.5)]" />
            <span className="font-head text-sm font-bold tracking-tight text-white">
              CYVR<span className="text-cyber-cyan"> LABS</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden font-mono-tech text-xs text-white/50 sm:block" data-testid="dash-user">{user?.email}</span>
            <Link to="/" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-cyber-cyan hover:text-cyber-cyan">
              <Home className="h-4 w-4" />
            </Link>
            <button
              onClick={logout}
              data-testid="dash-logout"
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-mono-tech text-xs uppercase tracking-widest text-white/70 transition-colors hover:border-[#ff2d6f] hover:text-[#ff2d6f]"
            >
              <LogOut className="h-3.5 w-3.5" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 md:px-10 md:py-14">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-head text-3xl font-bold tracking-tight text-white md:text-4xl"
        >
          Hola, {user?.name?.split(" ")[0] || "operador"}.
        </motion.h1>
        <p className="mt-2 font-sans text-white/55">Panel de operaciones</p>

        {/* Tabs */}
        <div className="mt-6 inline-flex gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1" data-testid="dash-tabs">
          {[
            { id: "scanner", label: "Escáner", icon: Radar },
            { id: "footprint", label: "Huella digital", icon: Fingerprint },
            ...(user?.is_admin
              ? [
                  { id: "leads", label: "Leads", icon: Inbox },
                  { id: "whatsapp", label: "WhatsApp Bot", icon: MessageCircle },
                ]
              : []),
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono-tech text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${
                tab === t.id ? "bg-cyber-cyan text-[#04060a]" : "text-white/60 hover:text-white"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Entitlements */}
        {(user?.priority_scans > 0 || user?.monitoring_active) && (
          <div className="mt-6 flex flex-wrap gap-3" data-testid="entitlements">
            {user?.priority_scans > 0 && (
              <div className="inline-flex items-center gap-2 rounded-full border border-cyber-cyan/40 bg-cyber-cyan/[0.08] px-4 py-2" data-testid="entitlement-priority">
                <Crown className="h-4 w-4 text-cyber-cyan" />
                <span className="font-mono-tech text-[11px] uppercase tracking-widest text-white/85">
                  {user.priority_scans} análisis contratado{user.priority_scans > 1 ? "s" : ""} disponible{user.priority_scans > 1 ? "s" : ""}
                </span>
              </div>
            )}
            {user?.monitoring_active && (
              <div className="inline-flex items-center gap-2 rounded-full border border-cyber-violet/40 bg-cyber-violet/[0.08] px-4 py-2" data-testid="entitlement-monitoring">
                <Repeat className="h-4 w-4 text-cyber-violet" />
                <span className="font-mono-tech text-[11px] uppercase tracking-widest text-white/85">
                  Vigilancia continua activa
                </span>
              </div>
            )}
            {user?.monitoring_active && (
              <button
                onClick={openPortal}
                data-testid="manage-subscription-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono-tech text-[11px] uppercase tracking-widest text-white/70 transition-colors hover:border-cyber-cyan hover:text-cyber-cyan"
              >
                <Settings className="h-4 w-4" /> Gestionar suscripción
              </button>
            )}
          </div>
        )}

        {tab === "footprint" ? (
          <div className="mt-8">
            <Footprint user={user} onProfile={updateUser} />
          </div>
        ) : tab === "leads" && user?.is_admin ? (
          <Leads />
        ) : tab === "whatsapp" && user?.is_admin ? (
          <div className="mt-8">
            <WhatsAppSim />
          </div>
        ) : (
        <div className="mt-8 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-8">
            <Scanner onResult={handleResult} priorityAvailable={user?.priority_scans || 0} onScanDone={refreshUser} />
            {result && <ScanResult scan={result} />}
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="mb-3 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-cyber-cyan">Mapa de amenazas</h3>
              <ThreatMap compact />
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="scan-history">
              <h3 className="flex items-center gap-2 font-head text-sm font-semibold text-white">
                <History className="h-4 w-4 text-cyber-cyan" /> Historial ({history.length})
              </h3>
              <div className="mt-4 space-y-2">
                {history.length === 0 && <p className="font-sans text-sm text-white/40">Aún no has hecho escaneos.</p>}
                {history.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => setResult(h)}
                    data-testid="history-item"
                    className="flex w-full items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.06]"
                  >
                    <span className="truncate font-mono-tech text-xs text-white/80">{h.target}</span>
                    <span className="ml-2 shrink-0 font-head text-sm font-bold" style={{ color: gradeColor(h.grade) }}>{h.grade}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        )}
      </main>
    </div>
  );
}
